import { useEffect, useState } from "react";
import { useFirebase } from "../context/FirebaseContext";
import {
  PROFILE_PRIVACY_MODES,
  PUBLIC_PROFILES_COLLECTION,
  buildPublicProfileDocument,
  canViewProfileFromState,
  isAdministratorIdentity,
  normalizeProfilePrivacyMode
} from "../utility/profilePrivacy";
import { getProfileDirectoryEntry } from "../utility/profileDirectoryApi";
import { DEMO_FRIEND_USER_ID, isDemoUserId } from "../utility/demoSession";
import type { AuthUser, ProfileUser, PrivacyMode } from "../types/profile";

interface ProfileAccessState {
  ownerId: string | null | undefined;
  loading: boolean;
  allowed: boolean;
  isAdmin: boolean;
  isConfirmedFriend: boolean;
  ownerHasViewer: boolean;
  viewerHasOwner: boolean;
  privacyMode: PrivacyMode;
  publicProfile: ProfileUser | null;
  error: string;
}
interface ProfileSnapshot {
  exists: boolean | (() => boolean);
  data: () => ProfileUser | undefined;
}

const initialAccessState = (ownerId: string | null | undefined = "", viewer: AuthUser | null | undefined = null): ProfileAccessState => {
  // The authenticated owner always has access to their own application
  // shell. Waiting for publicProfiles here put a Firestore round trip in
  // front of every private route (statistics, diary, lists, etc.). Privacy
  // metadata is only needed when somebody views another user's profile.
  const isOwner = Boolean(ownerId && viewer?.uid === ownerId);
  return {
    ownerId,
    loading: !isOwner,
    allowed: isOwner,
    isAdmin: false,
    isConfirmedFriend: false,
    ownerHasViewer: false,
    viewerHasOwner: false,
    privacyMode: PROFILE_PRIVACY_MODES.public,
    publicProfile: null,
    error: ""
  };
};

const snapshotExists = (snapshot: ProfileSnapshot) => Boolean(
  typeof snapshot?.exists === "function" ? snapshot.exists() : snapshot?.exists
);

const snapshotData = (snapshot: ProfileSnapshot) => (
  snapshotExists(snapshot) && typeof snapshot?.data === "function"
    ? snapshot.data() || {}
    : null
);

export const useProfileAccess = ({ ownerId, viewer }: { ownerId?: string | null; viewer?: AuthUser | null }) => {
  const { firestore } = useFirebase();
  const [state, setState] = useState(() => initialAccessState(ownerId, viewer));

  useEffect(() => {
    let active = true;
    const unsubscribers: { (): void; (): void; (): void; }[] = [];
    const cleanFriendListeners = () => {
      while (unsubscribers.length > 1) {
        const unsubscribe = unsubscribers.pop();
        try { unsubscribe?.(); } catch {}
      }
    };

    const update = (next: Partial<ProfileAccessState>) => {
      if (!active) return;
      setState((current) => ({ ...current, ownerId, ...next }));
    };

    if (!ownerId || !viewer?.uid || !firestore) {
      update({ loading: false, allowed: false, error: "access-unavailable" });
      return () => { active = false; };
    }

    if (viewer.uid === ownerId) {
      update(initialAccessState(ownerId, viewer));
      return () => { active = false; };
    }

    // The prepared comparison profile is deliberately private for every
    // ordinary account. Firestore independently requires the demo custom-token
    // claim for its data, while this client-side exception prevents that
    // private template marker from hiding the promised demo comparison UI.
    if (ownerId === DEMO_FRIEND_USER_ID && isDemoUserId(viewer.uid)) {
      update({
        loading: false,
        allowed: true,
        isAdmin: false,
        isConfirmedFriend: true,
        ownerHasViewer: true,
        viewerHasOwner: true,
        privacyMode: PROFILE_PRIVACY_MODES.public,
        publicProfile: {
          uid: DEMO_FRIEND_USER_ID,
          name: "Кинокритик Демо",
          displayName: "Кинокритик Демо",
          privacyMode: PROFILE_PRIVACY_MODES.public,
          isDemo: true
        },
        error: ""
      });
      return () => { active = false; };
    }

    const resolveAdministrator = async () => {
      if (isAdministratorIdentity(viewer)) return true;
      try {
        const tokenResult = await viewer.getIdTokenResult?.();
        return isAdministratorIdentity(viewer, { admin: tokenResult?.claims.admin });
      } catch {
        return false;
      }
    };

    const applyMetadata = (metadata: ProfileUser | null, isAdmin: boolean) => {
      cleanFriendListeners();
      const publicProfile = metadata
        ? { ...metadata, uid: metadata.uid || ownerId }
        : null;
      const privacyMode = normalizeProfilePrivacyMode(publicProfile?.privacyMode, {
        isDemo: publicProfile?.isDemo === true
      });
      const isOwner = viewer.uid === ownerId;

      if (
        isOwner ||
        isAdmin ||
        publicProfile?.isDemo === true ||
        privacyMode === PROFILE_PRIVACY_MODES.public
      ) {
        update({
          loading: false,
          allowed: true,
          isAdmin: Boolean(isAdmin && !isOwner),
          isConfirmedFriend: false,
          ownerHasViewer: false,
          viewerHasOwner: false,
          privacyMode,
          publicProfile,
          error: ""
        });
        return;
      }

      if (privacyMode !== PROFILE_PRIVACY_MODES.friends) {
        update({
          loading: false,
          allowed: false,
          isAdmin: false,
          isConfirmedFriend: false,
          ownerHasViewer: false,
          viewerHasOwner: false,
          privacyMode,
          publicProfile,
          error: ""
        });
        return;
      }

      // A one-sided follow/request is not friendship. Both documents must
      // exist, and live listeners revoke access immediately after removal.
      let ownerHasViewer = false;
      let viewerHasOwner = false;
      let ownerRelationReady = false;
      let viewerRelationReady = false;
      const publishFriendAccess = () => {
        if (!ownerRelationReady || !viewerRelationReady) return;
        const isConfirmedFriend = ownerHasViewer && viewerHasOwner;
        update({
          loading: false,
          allowed: canViewProfileFromState({
            viewerId: viewer.uid,
            ownerId,
            privacyMode,
            isConfirmedFriend
          }),
          isAdmin: false,
          isConfirmedFriend,
          ownerHasViewer,
          viewerHasOwner,
          privacyMode,
          publicProfile,
          error: ""
        });
      };
      const relationError = () => {
        update({
          loading: false,
          allowed: false,
          isAdmin: false,
          isConfirmedFriend: false,
          ownerHasViewer: false,
          viewerHasOwner: false,
          privacyMode,
          publicProfile,
          error: "friendship-check-failed"
        });
      };

      const ownerRelation = firestore
        .collection("users")
        .doc(ownerId)
        .collection("friends")
        .doc(viewer.uid);
      const viewerRelation = firestore
        .collection("users")
        .doc(viewer.uid)
        .collection("friends")
        .doc(ownerId);
      unsubscribers.push(ownerRelation.onSnapshot(
        (snapshot) => {
          ownerHasViewer = snapshotExists(snapshot);
          ownerRelationReady = true;
          publishFriendAccess();
        },
        relationError
      ));
      unsubscribers.push(viewerRelation.onSnapshot(
        (snapshot) => {
          viewerHasOwner = snapshotExists(snapshot);
          viewerRelationReady = true;
          publishFriendAccess();
        },
        relationError
      ));
    };

    const start = async () => {
      update(initialAccessState(ownerId, viewer));
      const isAdmin = await resolveAdministrator();
      if (!active) return;

      const applyUnavailableMetadata = async () => {
        // Production can still run the pre-publicProfiles ruleset. In that
        // configuration listening to publicProfiles is denied even for a
        // perfectly ordinary public account, while the legacy users document
        // remains readable. Always try that compatible source before the
        // optional HTTP directory.
        try {
          const legacySnapshot = await firestore
            .collection("users")
            .doc(ownerId)
            .get({ source: "server" });
          const legacyProfile = snapshotData(legacySnapshot);
          if (legacyProfile) {
            applyMetadata(buildPublicProfileDocument({
              uid: ownerId,
              profile: legacyProfile
            }), isAdmin);
            return;
          }
        } catch {}

        try {
          const directoryProfile = await getProfileDirectoryEntry({
            auth: { currentUser: viewer },
            uid: ownerId
          });
          if (directoryProfile) {
            applyMetadata(directoryProfile, isAdmin);
            return;
          }
          update({ loading: false, allowed: false, error: "profile-not-found" });
        } catch {
          // Missing privacy infrastructure is a legacy deployment state, not
          // a user privacy choice. Legacy accounts had public profiles by
          // default. Firestore rules still remain the final authority for
          // every underlying data read, so this fallback cannot expose a
          // genuinely private profile.
          applyMetadata({
            uid: ownerId,
            privacyMode: PROFILE_PRIVACY_MODES.public
          }, isAdmin);
        }
      };

      const publicProfileRef = firestore
        .collection(PUBLIC_PROFILES_COLLECTION)
        .doc(ownerId);
      const unsubscribeProfile = publicProfileRef.onSnapshot(
        async (snapshot) => {
          const metadata = snapshotData(snapshot);
          if (metadata) {
            applyMetadata(metadata, isAdmin);
            return;
          }

          // Existing accounts predate publicProfiles. Their missing privacy
          // field means public by specification. This server-only read is not
          // cached and fails closed if Rules deny it.
          try {
            const legacySnapshot = await firestore
              .collection("users")
              .doc(ownerId)
              .get({ source: "server" });
            const legacyProfile = snapshotData(legacySnapshot);
            if (!legacyProfile) {
              update({ loading: false, allowed: false, error: "profile-not-found" });
              return;
            }
            applyMetadata(buildPublicProfileDocument({
              uid: ownerId,
              profile: legacyProfile
            }), isAdmin);
          } catch {
            await applyUnavailableMetadata();
          }
        },
        () => {
          void applyUnavailableMetadata();
        }
      );
      unsubscribers.unshift(unsubscribeProfile);
    };

    void start();
    return () => {
      active = false;
      unsubscribers.forEach((unsubscribe) => {
        try { unsubscribe?.(); } catch {}
      });
    };
  }, [firestore, ownerId, viewer]);

  return state;
};

export default useProfileAccess;
